import ApiService from "@/services/api.service";

// ACTIONS
export const FETCH_LIST = "FETCH_LIST";
export const DELETE = "DELETE";
export const ACTIVATE = "ACTIVATE";
// Fase 4i — prezzo dedicato al cliente (PUT /api/ListinoClienti/{id}).
export const AGGIORNA_PREZZO = "AGGIORNA_PREZZO";
// Fase B — date di decorrenza e dismissione (PUT /api/ListinoClienti/{id}/date).
export const AGGIORNA_DATE = "AGGIORNA_DATE";
// Fase 4b — letture di supporto alla pagina "Servizi attivi del cliente".
export const FETCH_LISTINI_BASE = "FETCH_LISTINI_BASE";
export const FETCH_SERVIZI = "FETCH_SERVIZI";
export const FETCH_PERIODI = "FETCH_PERIODI";
export const FETCH_CONSUMI = "FETCH_CONSUMI";

// MUTATIONS
export const SET_LIST = "SET_LIST";
export const SET_LOADING = "SET_LOADING";
export const SET_ERROR = "SET_ERROR";
export const SET_LISTINO_BASE = "SET_LISTINO_BASE";
export const SET_SERVIZI = "SET_SERVIZI";
export const SET_PERIODI = "SET_PERIODI";
export const SET_CONSUMI = "SET_CONSUMI";
export const SET_LOADING_SUPPORTO = "SET_LOADING_SUPPORTO";
export const SET_ERRORE_CARICAMENTO = "SET_ERRORE_CARICAMENTO";

// Controller di backend: [Route("api/[controller]")] -> /api/ListinoClienti.
// La baseURL di axios è VUE_APP_ROOT_API (default "/api").
const RESOURCE_CLIENTI = "ListinoClienti";
const RESOURCE_LISTINO_BASE = "Listino";
const RESOURCE_SERVIZI = "Servizio";
const RESOURCE_FATTURAZIONE = "FatturazioneRighe";

// Testo usato quando l'errore non contiene un messaggio leggibile.
const MESSAGGIO_ERRORE_GENERICO =
    "Si è verificato un errore, riprova più tardi.";

// Il backend risponde agli errori con HTTP 500 e body {Status, Message}
// (Response<object> di questa codebase). A seconda della configurazione JSON il
// campo arriva come "message" o "Message": si accettano entrambe le forme, più le
// varianti ProblemDetails, e in ogni caso il chiamante riceve una stringa.
const toMessage = (err) => {
    if (typeof err === "string") {
        return err.trim() || MESSAGGIO_ERRORE_GENERICO;
    }
    if (!err) {
        return MESSAGGIO_ERRORE_GENERICO;
    }

    const candidati = [
        err.response?.data?.message,
        err.response?.data?.Message,
        err.response?.data?.detail,
        err.response?.data?.title,
        err.response?.data,
        err.data?.message,
        err.data?.Message,
        err.detail,
        err.title,
        err.message,
    ];
    const messaggio = candidati.find(
        (valore) => typeof valore === "string" && valore.trim() !== ""
    );
    if (messaggio) {
        return messaggio.trim();
    }

    try {
        const serializzato = JSON.stringify(err);
        if (serializzato && serializzato !== "{}" && serializzato !== "[]") {
            return serializzato;
        }
    } catch {
        // Oggetto non serializzabile: resta il messaggio generico.
    }

    return MESSAGGIO_ERRORE_GENERICO;
};

const state = {
    // Pagina del listino per cliente (ListinoClientiDto): {items, totalCount}.
    list: {},
    loading: false,
    error: {},
    // Fase 4b — dati di supporto della pagina "Servizi attivi del cliente".
    listinoBase: [],
    servizi: [],
    periodi: [],
    consumi: [],
    // Esattamente un caricamento di supporto alla volta è attivo:
    // false = tutti fermi.
    loadingSupporto: false,
    // Errore del caricamento: stringa (messaggio) oppure null.
    // Distingue i dati realmente vuoti dal caricamento fallito.
    erroreCaricamento: null,
};

const getters = {
    getList: (state) => state.list,
    getLoading: (state) => state.loading,
    error: (state) => state.error,
    // Messaggio dell'ultima operazione (attivazione/dismissione) come stringa,
    // oppure null: le azioni esistenti scrivono in `error` solo un campo, qui
    // vengono accettate entrambe le forme del body di errore del backend.
    getErrore: (state) => {
        if (typeof state.error === "string") {
            return state.error.trim() || null;
        }
        if (!state.error || !Object.keys(state.error).length) {
            return null;
        }
        return toMessage(state.error);
    },
    getListinoBase: (state) => state.listinoBase,
    getServizi: (state) => state.servizi,
    getPeriodi: (state) => state.periodi,
    getConsumi: (state) => state.consumi,
    getLoadingSupporto: (state) => state.loadingSupporto,
    getErroreCaricamento: (state) => state.erroreCaricamento,
};

// Fase 6c — payload di FETCH_LIST. L'azione è condivisa con
// detailAccountPec.vue, che invia la sola query come stringa: quella forma
// resta identica (nessun parametro aggiunto) e il comportamento di quel
// consumatore non cambia.
// Forma nuova: { query, includiCessate }. Il parametro opzionale
// `includiCessate` del backend (GET /api/ListinoClienti) è accodato alla query
// SOLO quando vale esattamente `true`: assente o `false` = query di oggi.
const queryListinoClienti = (payload) => {
    if (payload === null || typeof payload !== "object") return payload;

    const query = typeof payload.query === "string" ? payload.query : "";
    if (payload.includiCessate !== true) return query;
    return query ? `${query}&includiCessate=true` : "includiCessate=true";
};

const actions = {
    // ---------------------------------------------------------------------
    // Azioni esistenti della pagina Listini: comportamento invariato.
    // ---------------------------------------------------------------------
    [FETCH_LIST]({ commit }, payload) {
        commit(SET_LOADING, true);

        return new Promise((resolve) => {
            ApiService.get(`${RESOURCE_CLIENTI}?${queryListinoClienti(payload)}`)
                .then((res) => {
                    commit(SET_LIST, res.data);
                    commit(SET_LOADING, false);
                    resolve(res);
                })
                .catch(({ response }) => {
                    commit(SET_ERROR, response?.data?.message);
                    setTimeout(() => {
                        commit(SET_ERROR, "");
                        commit(SET_LOADING, false);
                    }, 5000);
                });
        });
    },

    [DELETE]({ commit }, params) {
        return new Promise((resolve) => {
            ApiService.delete(
                `${RESOURCE_CLIENTI}/${params.idListino}`,
                params.idCliente
            )
                .then((res) => {
                    resolve(res);
                })
                .catch(({ response }) => {
                    commit(SET_ERROR, response?.data?.message);
                    setTimeout(() => commit(SET_ERROR, ""), 5000);
                });
        });
    },

    [ACTIVATE]({ commit }, params) {
        return new Promise((resolve, reject) => {
            ApiService.post(RESOURCE_CLIENTI, params)
                .then((res) => {
                    resolve(res);
                })
                .catch(({ response }) => {
                    commit(SET_ERROR, response?.data?.message);
                    setTimeout(() => commit(SET_ERROR, ""), 5000);
                    reject(response);
                });
        });
    },

    // ---------------------------------------------------------------------
    // Fase 4i — prezzo dedicato al cliente.
    // PUT /api/ListinoClienti/{id}: `id` è la chiave della riga
    // (ListinoClientiDto.id), cioè l'unico identificatore univoco quando
    // coesistono la riga cessata e quella riattivata: NON la coppia
    // listino/cliente.
    // Il PUT sovrascrive price E unitaincluse: il chiamante deve inviarli
    // sempre entrambi, altrimenti il campo omesso viene azzerato.
    // Accetta solo le righe in corso (validTo nullo): su una riga cessata il
    // backend risponde 500 con "La riga è cessata: ...".
    // ---------------------------------------------------------------------
    [AGGIORNA_PREZZO]({ commit }, { id, body }) {
        return new Promise((resolve, reject) => {
            ApiService.update(RESOURCE_CLIENTI, id, body)
                .then((res) => {
                    resolve(res);
                })
                .catch((error) => {
                    commit(SET_ERROR, toMessage(error));
                    setTimeout(() => commit(SET_ERROR, ""), 5000);
                    reject(error);
                });
        });
    },

    // ---------------------------------------------------------------------
    // Fase B — modifica delle date di decorrenza e dismissione.
    // PUT /api/ListinoClienti/{id}/date: `id` è la chiave della riga
    // (ListinoClientiDto.id), non la coppia listino/cliente.
    // Body: { validFrom, validTo, motivo } — `null` in un campo significa
    // "campo NON modificato" (non azzera la data). La dismissione inviata
    // viene normalizzata dal backend al PRIMO GIORNO DEL MESE SUCCESSIVO
    // alla data scelta (15/09 -> 01/10), come la dismissione di oggi.
    // Accetta solo le righe in corso (validTo nullo); il motivo finisce
    // solo nel log del backend.
    // ---------------------------------------------------------------------
    [AGGIORNA_DATE]({ commit }, { id, body }) {
        return new Promise((resolve, reject) => {
            ApiService.update(RESOURCE_CLIENTI, `${id}/date`, body)
                .then((res) => {
                    resolve(res);
                })
                .catch((error) => {
                    commit(SET_ERROR, toMessage(error));
                    setTimeout(() => commit(SET_ERROR, ""), 5000);
                    reject(error);
                });
        });
    },

    // ---------------------------------------------------------------------
    // Fase 4b — letture di supporto. Nessuna di queste azioni modifica le
    // azioni precedenti né lo stato `list`, che resta la pagina del cliente.
    // Tutte risolvono (array vuoto in caso di errore): l'errore è esposto da
    // getErroreCaricamento, così la pagina distingue "vuoto" da "fallito".
    // ---------------------------------------------------------------------

    // GET /api/Listino — listino base, senza parametri e senza paginazione.
    // Serve per codice, frequenza e descrizione del servizio di ogni riga.
    [FETCH_LISTINI_BASE](context) {
        context.commit(SET_LOADING_SUPPORTO, true);
        context.commit(SET_ERRORE_CARICAMENTO, null);

        return new Promise((resolve) => {
            ApiService.get(RESOURCE_LISTINO_BASE)
                .then(({ data }) => {
                    context.commit(SET_LISTINO_BASE, data);
                    context.commit(SET_ERRORE_CARICAMENTO, null);
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_LISTINO_BASE, []);
                    context.commit(SET_ERRORE_CARICAMENTO, toMessage(error));
                    resolve([]);
                })
                .finally(() =>
                    context.commit(SET_LOADING_SUPPORTO, false)
                );
        });
    },

    // GET /api/Servizio — catalogo servizi (frequenza, tipo di calcolo e flag
    // del rateo della prima attivazione).
    [FETCH_SERVIZI](context) {
        context.commit(SET_LOADING_SUPPORTO, true);

        return new Promise((resolve) => {
            ApiService.get(RESOURCE_SERVIZI)
                .then(({ data }) => {
                    context.commit(SET_SERVIZI, data);
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_SERVIZI, []);
                    context.commit(SET_ERRORE_CARICAMENTO, toMessage(error));
                    resolve([]);
                })
                .finally(() =>
                    context.commit(SET_LOADING_SUPPORTO, false)
                );
        });
    },

    // GET /api/FatturazioneRighe/periodi — periodi elaborati, ordinati dal
    // più recente: {year, month, numeroRighe, calcolatoIlMax}.
    [FETCH_PERIODI](context) {
        context.commit(SET_LOADING_SUPPORTO, true);

        return new Promise((resolve) => {
            ApiService.get(`${RESOURCE_FATTURAZIONE}/periodi`)
                .then(({ data }) => {
                    context.commit(SET_PERIODI, data);
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_PERIODI, []);
                    context.commit(SET_ERRORE_CARICAMENTO, toMessage(error));
                    resolve([]);
                })
                .finally(() =>
                    context.commit(SET_LOADING_SUPPORTO, false)
                );
        });
    },

    // GET /api/FatturazioneRighe?anno=&mese=&idCliente=
    // anno e mese sono obbligatori; idCliente è passato solo da un superadmin
    // (per gli altri utenti il backend applica da solo il proprio cliente e va
    // in errore se il filtro arriva dalla richiesta).
    [FETCH_CONSUMI](context, { anno, mese, idCliente }) {
        context.commit(SET_LOADING_SUPPORTO, true);

        const parametri = [
            `anno=${encodeURIComponent(anno)}`,
            `mese=${encodeURIComponent(mese)}`,
        ];
        if (idCliente !== null && idCliente !== undefined && idCliente !== "") {
            parametri.push(`idCliente=${encodeURIComponent(idCliente)}`);
        }

        return new Promise((resolve) => {
            ApiService.get(
                `${RESOURCE_FATTURAZIONE}?${parametri.join("&")}`
            )
                .then(({ data }) => {
                    context.commit(SET_CONSUMI, data);
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_CONSUMI, []);
                    context.commit(SET_ERRORE_CARICAMENTO, toMessage(error));
                    resolve([]);
                })
                .finally(() =>
                    context.commit(SET_LOADING_SUPPORTO, false)
                );
        });
    },
};

const mutations = {
    [SET_LIST](state, list) {
        state.list = list;
    },
    [SET_LOADING](state, val) {
        state.loading = val;
    },
    [SET_ERROR](state, val) {
        state.error = val;
    },
    [SET_LISTINO_BASE](state, list) {
        state.listinoBase = Array.isArray(list) ? list : [];
    },
    [SET_SERVIZI](state, list) {
        state.servizi = Array.isArray(list) ? list : [];
    },
    [SET_PERIODI](state, list) {
        state.periodi = Array.isArray(list) ? list : [];
    },
    [SET_CONSUMI](state, list) {
        state.consumi = Array.isArray(list) ? list : [];
    },
    [SET_LOADING_SUPPORTO](state, val) {
        state.loadingSupporto = val;
    },
    // Sempre null oppure stringa: distingue i dati vuoti dal caricamento fallito.
    [SET_ERRORE_CARICAMENTO](state, valore) {
        state.erroreCaricamento =
            typeof valore === "string" && valore !== "" ? valore : null;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
